import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ImageOff, Loader2, Pencil, Save, X, ImagePlus, CheckSquare, Square, Trash2, Printer, Camera, Shirt } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { entryThumbSrc, formatRelativeTime } from "../lib/media";
import { printContinuitySheet, buildPrintItem } from "../lib/printSheet";
import { Button } from "./ui/button";
import PrintLayoutDialog from "./PrintLayoutDialog";
import SegmentedControl from "./SegmentedControl";
import AllergyBadge from "./AllergyBadge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * ContinuityBlock — one (character × scene) card.
 *
 * Used in TWO places with the SAME shape:
 *   Scene view   → header is the character (tap → /characters/:id)
 *   Character view → header is the scene  (tap → /scenes/:id)
 *
 * It always renders an image grid + scene-specific notes textarea, no
 * matter which way the user arrived. Notes save explicitly via the
 * pen → save flow so users can correct typos before persisting on
 * patchy on-set wifi.
 *
 * Props:
 *   projectId, sceneId, characterId — the (s, c) pair
 *   header     {kind: "character"|"scene", primary: ReactNode, secondary: ReactNode, to: string}
 *   notes      initial notes string
 *   notesUpdatedAt, notesUpdatedByName — attribution for last edit
 *   entries    [state_entry]           — already filtered to this scene+character
 *   backHref, backLabel                — passed into ImageDetail link state
 *   onCapture(action)                  — fires capture for this scene+character
 *   onNotesChange(notesShape)          — bubble up after save so parent updates
 */
export default function ContinuityBlock({
    projectId,
    sceneId,
    characterId,
    header,
    notes,
    notesUpdatedAt,
    notesUpdatedByName,
    entries,
    backHref,
    backLabel,
    onCapture,
    onNotesChange,
    canEditNotes = true,
    // Iter-130b — when supplied, the multi-image print flow uses
    // these fields for clean Scene · Look · Notes metadata. Optional
    // to keep older callers (and the lighter Scene/Character views)
    // working without churn.
    scene = null,
    // Iter-158.13.2.20f.33 — Character-level allergy note. When
    // provided, the block renders a small "✚ Allergy" safety
    // indicator adjacent to the character sub-header (i.e. only
    // when `header.kind === "character"`, on Scene view). Clicking
    // navigates to `${routeBase}/${projectId}/characters/${characterId}#allergy`
    // so the source note is one tap away. Character page uses its
    // own header badge; this prop drives the Scene-view surfacing
    // only. When null/undefined the block renders nothing extra.
    allergyNote = null,
}) {
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(notes || "");
    const [saving, setSaving] = useState(false);
    const taRef = useRef(null);

    // Iter-118.5 — Bulk-select mode for fast cleanup of mistakenly
    // assigned images. Toggling on flips image cards from
    // navigation-on-tap to selection-on-tap. The action bar surfaces
    // inside the block so the user never loses context (which scene,
    // which character) while pruning links.
    // Iter-130b — same selection state now also feeds the multi-image
    // print flow (Print button next to Remove).
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [printDialogOpen, setPrintDialogOpen] = useState(false);

    // Iter-158.13.2.20f.33d — CONTEXTUAL working-mode default.
    // .20f.33c hard-coded useState("onset") to kill the .20f.31
    // shared-localStorage regression (Fittings visit → Fittings
    // sticks globally). Real-iPhone UAT rejected that too:
    // Fittings → Scene → Character should default to Fittings, not
    // to On Set. Alex's governing rule: "The default must derive
    // from the actual workflow/context through which the user
    // entered the Scene, not stale global state."
    //
    // CHECK FIRST — existing contextual signal already exists:
    // both OnSetMode and PrepMode pass `state.returnTo.path` when
    // linking to a Scene (OnSetMode.jsx:301, PrepMode.jsx:462/497).
    // The path ends with "/onset" or "/prep" and travels via React
    // Router `Link state` — ephemeral, per-navigation, NOT global,
    // NOT localStorage. SceneDetail already reads it for the back
    // button. We reuse it here for the default working-mode pick.
    // Manual toggle preserved. Deep-links / bookmarks / any surface
    // without state fall back to "onset".
    const _location = useLocation();
    const _returnPath = _location?.state?.returnTo?.path || "";
    const _defaultWorkingMode = _returnPath.endsWith("/prep") ? "prep" : "onset";
    const [workingMode, setWorkingMode] = useState(_defaultWorkingMode);

    // Reset selection state any time the entry list shape changes
    // (e.g. parent refetch after a remove). Keeps the UI honest —
    // no stale checkboxes pointing at deleted ids.
    useEffect(() => {
        setSelectedIds((prev) => {
            const next = new Set();
            const ids = new Set((entries || []).map((e) => e.id));
            for (const id of prev) if (ids.has(id)) next.add(id);
            return next;
        });
    }, [entries]);

    // Iter-158.13.2.20f.31 — leaving one working mode should not
    // carry selection state into the other. Switching mode empties
    // any in-progress selection so `{selectedIds.size} of N` never
    // references invisible ids and Remove/Print never touches hidden
    // media.
    useEffect(() => {
        setSelectMode(false);
        setSelectedIds(new Set());
    }, [workingMode]);

    const exitSelectMode = () => {
        setSelectMode(false);
        setSelectedIds(new Set());
    };

    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllVisible = () => {
        // Iter-158.13.2.20f.31 — bulk-select is scoped to the block's
        // *currently visible* working-mode bucket. Selecting hidden
        // media would violate the deliberate-toggle UX ("I only see
        // Fittings; I can only bulk-remove Fittings").
        setSelectedIds(new Set(_visibleEntriesForSelect().map((e) => e.id)));
    };

    // Iter-158.13.2.20f.31 — visible entries resolver used by
    // selectAllVisible (called before render). Duplicates the tiny
    // filter used in the render body; kept as a private helper so the
    // handler doesn't need to close over render-scope variables.
    const _visibleEntriesForSelect = () => {
        const list = entries || [];
        return workingMode === "prep"
            ? list.filter((e) => e.mode === "prep")
            : list.filter((e) => e.mode === "onset");
    };

    const performBulkRemove = async () => {
        if (removing || selectedIds.size === 0) return;
        setRemoving(true);
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/state-entries/bulk-remove-link`,
                { entry_ids: Array.from(selectedIds) },
            );
            const removed = (data?.deleted?.length || 0) + (data?.demoted_to_unassigned?.length || 0);
            const skipped = data?.skipped?.length || 0;
            const demoted = data?.demoted_to_unassigned?.length || 0;
            const msg = removed > 0
                ? `Removed ${removed} link${removed === 1 ? "" : "s"}` +
                  (demoted > 0 ? ` · ${demoted} moved to Unassigned` : "")
                : "Nothing to remove";
            if (skipped > 0 && removed === 0) {
                toast.message(msg, {
                    description: `${skipped} skipped (already unassigned or no permission).`,
                });
            } else {
                toast.success(msg);
            }
            setConfirmRemove(false);
            exitSelectMode();
            // Notify the rest of the app so SceneDetail / CharacterDetail /
            // ProjectHome activity feed all re-fetch their data.
            window.dispatchEvent(
                new CustomEvent("onsetflow:state-entry-saved"),
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRemoving(false);
        }
    };

    useEffect(() => {
        // Sync the draft when parent feeds in fresh notes (e.g. after a
        // network refetch). Only sync when not actively editing so we
        // don't blow away the user's in-progress text.
        if (!editing) setDraft(notes || "");
    }, [notes, editing]);

    useEffect(() => {
        if (editing && taRef.current) {
            taRef.current.focus();
            // Place cursor at end so adding to existing notes is fast.
            const len = taRef.current.value.length;
            taRef.current.setSelectionRange(len, len);
        }
    }, [editing]);

    const startEdit = () => {
        setDraft(notes || "");
        setEditing(true);
    };

    const cancel = () => {
        setDraft(notes || "");
        setEditing(false);
    };

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const { data } = await api.put(
                `${apiBase}/${projectId}/scenes/${sceneId}/characters/${characterId}/notes`,
                { notes: draft },
            );
            onNotesChange?.(data);
            setEditing(false);
            toast.success("Notes saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const isCharacterHeader = header.kind === "character";
    const dataTestId = isCharacterHeader
        ? `scene-character-block-${characterId}`
        : `character-scene-block-${sceneId}`;

    // Iter-158.13.2.20f.31 — hoisted bucket + visible-entries
    // derivation. `entries` is the mode-agnostic combined stream
    // (.20f.27c). The working-mode segmented toggle picks which
    // bucket the block actively renders; the other bucket is
    // reachable one tap away without leaving the (scene, character)
    // pair. Both toolbar count and body grid consume `visibleEntries`
    // so the block is internally coherent. Header-level counts on
    // SceneDetail / CharacterDetail intentionally continue to reflect
    // the full photographic record (per Alex's .20f.31 refinement:
    // no cross-block synchronisation).
    const onsetBucket = (entries || []).filter((e) => e.mode === "onset");
    const prepBucket = (entries || []).filter((e) => e.mode === "prep");
    const visibleEntries = workingMode === "prep" ? prepBucket : onsetBucket;

    return (
        <section
            data-testid={dataTestId}
            className="rounded-xl border border-white/10 bg-card overflow-hidden"
        >
            {/* Header row — primary identity tap-target navigates to the
                opposite continuity surface. Capture button on the right
                pre-fills mode + character. */}
            <div className="flex items-center gap-3 p-4 border-b border-white/5">
                <Link
                    to={header.to}
                    state={{
                        returnTo: {
                            path: backHref,
                            label: backLabel
                                ? `Back to ${backLabel}`
                                : "Back",
                        },
                    }}
                    data-testid={`${dataTestId}-link`}
                    className="tap flex-1 min-w-0 flex items-center gap-3 -m-2 p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                    <div
                        className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center font-display font-bold ${
                            isCharacterHeader
                                ? "bg-primary/15 text-primary"
                                : "bg-secondary text-white text-sm"
                        }`}
                    >
                        {header.avatar}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-white font-semibold leading-tight truncate">
                            {header.primary}
                        </div>
                        {header.secondary ? (
                            <div className="text-[11px] text-neutral-500 truncate mt-0.5">
                                {header.secondary}
                            </div>
                        ) : null}
                    </div>
                </Link>
                {/* Iter-158.13.2.20f.33 — H&M allergy safety indicator.
                    Rendered only when a Character-level allergy note
                    exists for this character AND the block is
                    rendering a Character sub-header (Scene view).
                    Sits outside the tap-area of the block-header
                    Link so the badge click has its own destination:
                    the Character page anchor-scrolled to the source
                    note. Character page uses its own header badge;
                    this file only surfaces the Scene-view path. */}
                {isCharacterHeader && allergyNote ? (
                    <AllergyBadge
                        note={allergyNote}
                        to={`${routeBase}/${projectId}/characters/${characterId}#allergy`}
                        testid={`${dataTestId}-allergy-badge`}
                    />
                ) : null}
                {/* Iter-124 — contextual Upload action restored.
                    Exactly ONE button per row (no more Capture +
                    Upload duplication that iter-123 tore down). It
                    fires the scene+character-scoped upload so the
                    image lands on the right slot without the user
                    re-picking context after the file dialog returns.
                    The legacy `{dataTestId}-capture` testid is kept
                    so prior automation still targets the primary tap
                    surface. */}
                {onCapture ? (
                    <button
                        type="button"
                        data-testid={`${dataTestId}-capture`}
                        data-action="upload"
                        onClick={() => onCapture("photos")}
                        title="Upload image to this scene + character"
                        aria-label="Upload image"
                        className="shrink-0 tap inline-flex items-center justify-center h-9 w-9 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                        <ImagePlus size={14} />
                    </button>
                ) : null}
            </div>

            {/* Image grid — visually primary. Horizontal scroll on phone
                so a long continuity progression doesn't push the notes
                way down the page. */}
            <div className="p-3">
                {/* Iter-118.5 — Bulk-select toolbar. Inline above the
                    grid so the (scene, character) context is still
                    visible while the user picks which images to
                    remove. Shown only when there's >0 entries; the
                    "Select" button toggles selectMode and stays out
                    of the way otherwise.
                    Iter-158.13.2.20f.31 — guard now uses
                    `visibleEntries.length` so the Select affordance
                    only appears for the currently-active working
                    mode. When the user is on a mode that happens to
                    have zero media (e.g. On Set is empty but
                    Fittings has one image), the toolbar hides and
                    the toggle segments alone carry the discovery.
                    */}
                {visibleEntries.length > 0 ? (
                    <div
                        data-testid={`${dataTestId}-select-toolbar`}
                        className="flex items-center justify-between gap-2 mb-2"
                    >
                        {selectMode ? (
                            <>
                                <div className="flex items-center gap-1.5">
                                    <span
                                        data-testid={`${dataTestId}-select-count`}
                                        className="text-[11px] uppercase tracking-[0.2em] text-neutral-400"
                                    >
                                        {selectedIds.size} of {visibleEntries.length}
                                    </span>
                                    {selectedIds.size < visibleEntries.length ? (
                                        <button
                                            type="button"
                                            data-testid={`${dataTestId}-select-all`}
                                            onClick={selectAllVisible}
                                            className="instant-tap text-[11px] text-primary hover:underline"
                                        >
                                            Select all
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid={`${dataTestId}-select-none`}
                                            onClick={() => setSelectedIds(new Set())}
                                            className="instant-tap text-[11px] text-neutral-300 hover:underline"
                                        >
                                            Deselect all
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        data-testid={`${dataTestId}-select-cancel`}
                                        onClick={exitSelectMode}
                                        className="instant-tap inline-flex items-center justify-center h-8 px-2.5 rounded-md text-[11px] text-neutral-300 hover:text-white hover:bg-white/5"
                                    >
                                        Cancel
                                    </button>
                                    {/* Iter-130b — Print selected. Same
                                        select-mode that powers bulk
                                        remove now also feeds a clean
                                        multi-image print sheet. The
                                        layout chooser opens before any
                                        print job actually runs. */}
                                    <button
                                        type="button"
                                        data-testid={`${dataTestId}-bulk-print`}
                                        disabled={selectedIds.size === 0}
                                        onClick={() => setPrintDialogOpen(true)}
                                        title="Print selected images on a clean continuity sheet (Scene · Look · Notes)"
                                        className="instant-tap inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-secondary border border-white/15 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-neutral-100 text-[11px] font-semibold"
                                    >
                                        <Printer size={12} />
                                        Print
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`${dataTestId}-bulk-remove`}
                                        disabled={selectedIds.size === 0}
                                        onClick={() => setConfirmRemove(true)}
                                        className="instant-tap inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-red-600/85 hover:bg-red-500 disabled:bg-red-900/40 disabled:text-red-200/40 disabled:cursor-not-allowed text-white text-[11px] font-semibold"
                                    >
                                        <Trash2 size={12} />
                                        Remove
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <span
                                    data-testid={`${dataTestId}-visible-count`}
                                    className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                                >
                                    {visibleEntries.length} image{visibleEntries.length === 1 ? "" : "s"}
                                </span>
                                <button
                                    type="button"
                                    data-testid={`${dataTestId}-select-toggle`}
                                    onClick={() => setSelectMode(true)}
                                    title="Select images to remove from this scene/character"
                                    className="instant-tap inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11px] text-neutral-300 hover:text-white hover:bg-white/5 border border-white/10"
                                >
                                    <CheckSquare size={12} />
                                    Select
                                </button>
                            </>
                        )}
                    </div>
                ) : null}

                {entries.length === 0 ? (
                    <div
                        data-testid={`${dataTestId}-empty`}
                        className="rounded-md border border-dashed border-white/10 px-3 py-5 text-center text-[11px] text-neutral-500"
                    >
                        No continuity images yet.
                    </div>
                ) : (() => {
                    // Iter-158.13.2.20f.31 — H&M working-mode
                    // segmented toggle. `entries` is the mode-
                    // agnostic combined stream returned by
                    // `_entries_grouped` (.20f.27c). Buckets are
                    // hoisted above; here we render:
                    //   1. SegmentedControl [ On Set N | Fittings N ]
                    //      always visible (both segments, even at
                    //      count 0) so switching is always
                    //      discoverable — supersedes .20f.27c's
                    //      "hide toggle when single-provenance" UX.
                    //   2. A single flat grid of the current mode's
                    //      bucket, using the pre-.27c `-grid` testid
                    //      as the canonical single-grid anchor.
                    //   3. A per-mode "No X captures yet" empty card
                    //      when the current bucket is empty but the
                    //      other one has media.
                    const modeOptions = [
                        {
                            value: "onset",
                            label: "On Set",
                            icon: Camera,
                            count: onsetBucket.length,
                            testid: `${dataTestId}-working-mode-onset`,
                        },
                        {
                            value: "prep",
                            label: "Fittings",
                            icon: Shirt,
                            count: prepBucket.length,
                            testid: `${dataTestId}-working-mode-prep`,
                        },
                    ];

                    const renderTile = (e) => {
                        const thumb = entryThumbSrc(projectId, e, apiBase);
                        const isSelected = selectedIds.has(e.id);
                        const cardClass = `block relative aspect-square rounded-md overflow-hidden bg-black border transition-colors ${
                            selectMode
                                ? isSelected
                                    ? "border-primary ring-2 ring-primary"
                                    : "border-white/10 hover:border-white/30"
                                : "border-white/10 hover:border-white/30"
                        }`;
                        const inner = (
                            <>
                                {thumb ? (
                                    <img
                                        src={thumb}
                                        alt={e.note || "capture"}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-neutral-600">
                                        <ImageOff size={18} />
                                    </div>
                                )}
                                {selectMode ? (
                                    <span
                                        data-testid={`${dataTestId}-entry-select-${e.id}`}
                                        data-selected={isSelected ? "true" : "false"}
                                        className={`absolute top-1 right-1 h-5 w-5 rounded-md inline-flex items-center justify-center ${
                                            isSelected
                                                ? "bg-primary text-primary-foreground"
                                                : "bg-black/60 text-white border border-white/40"
                                        }`}
                                    >
                                        {isSelected ? (
                                            <CheckSquare size={12} />
                                        ) : (
                                            <Square size={12} />
                                        )}
                                    </span>
                                ) : null}
                                {e.look ? (
                                    <span
                                        data-testid={`${dataTestId}-entry-look-${e.id}`}
                                        className="absolute left-1 right-1 bottom-1 px-1.5 py-0.5 rounded-md bg-black/65 backdrop-blur-sm text-[10px] text-white truncate"
                                        title={e.look}
                                    >
                                        {e.look}
                                    </span>
                                ) : null}
                                {e.not_in_script ? (
                                    <span
                                        data-testid={`${dataTestId}-entry-not-in-script-${e.id}`}
                                        title="This character is not part of the scripted scene."
                                        className="absolute left-1 top-1 px-1 py-0.5 rounded bg-amber-400/90 text-black text-[9px] font-bold uppercase tracking-wider"
                                    >
                                        !
                                    </span>
                                ) : null}
                            </>
                        );
                        return (
                            <li key={e.id}>
                                {selectMode ? (
                                    <button
                                        type="button"
                                        data-testid={`${dataTestId}-entry-${e.id}`}
                                        data-selected={isSelected ? "true" : "false"}
                                        onClick={() => toggleSelected(e.id)}
                                        aria-pressed={isSelected}
                                        className={cardClass + " w-full text-left"}
                                    >
                                        {inner}
                                    </button>
                                ) : (
                                    <Link
                                        to={`${routeBase}/${projectId}/state-entries/${e.id}`}
                                        state={{ backHref, backLabel }}
                                        data-testid={`${dataTestId}-entry-${e.id}`}
                                        className={cardClass}
                                    >
                                        {inner}
                                    </Link>
                                )}
                            </li>
                        );
                    };

                    const emptyLabel = workingMode === "prep"
                        ? "No Fittings captures yet"
                        : "No On Set captures yet";

                    return (
                        <div className="space-y-3">
                            <SegmentedControl
                                variant="tabs"
                                value={workingMode}
                                onChange={setWorkingMode}
                                options={modeOptions}
                                ariaLabel="Working continuity mode"
                                testid={`${dataTestId}-working-mode`}
                            />
                            {visibleEntries.length === 0 ? (
                                <div
                                    data-testid={`${dataTestId}-working-mode-empty`}
                                    className="rounded-md border border-dashed border-white/10 px-3 py-5 text-center text-[11px] text-neutral-500"
                                >
                                    {emptyLabel}
                                </div>
                            ) : (
                                <ul
                                    data-testid={`${dataTestId}-grid`}
                                    className="grid grid-cols-3 sm:grid-cols-4 gap-2"
                                >
                                    {visibleEntries.map(renderTile)}
                                </ul>
                            )}
                        </div>
                    );
                })()}
            </div>

            {/* Notes — secondary. Display-first; pencil reveals editor. */}
            <div className="px-4 pb-4">
                <div className="flex items-center justify-between mb-1.5">
                    <h3 className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                        Continuity notes
                    </h3>
                    {editing ? (
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                data-testid={`${dataTestId}-notes-cancel`}
                                onClick={cancel}
                                disabled={saving}
                                className="tap inline-flex items-center justify-center h-7 w-7 rounded-md text-neutral-400 hover:text-white hover:bg-white/5"
                            >
                                <X size={13} />
                            </button>
                            <Button
                                type="button"
                                data-testid={`${dataTestId}-notes-save`}
                                onClick={save}
                                disabled={saving || draft === (notes || "")}
                                className="h-7 px-2 text-[11px] font-semibold"
                            >
                                {saving ? (
                                    <Loader2 size={11} className="animate-spin" />
                                ) : (
                                    <>
                                        <Save size={11} className="mr-1" />
                                        Save
                                    </>
                                )}
                            </Button>
                        </div>
                    ) : canEditNotes ? (
                        <button
                            type="button"
                            data-testid={`${dataTestId}-notes-edit`}
                            onClick={startEdit}
                            className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-neutral-400 hover:text-white hover:bg-white/5"
                        >
                            <Pencil size={11} />
                            {notes ? "Edit" : "Add"}
                        </button>
                    ) : null}
                </div>
                {editing ? (
                    <textarea
                        ref={taRef}
                        data-testid={`${dataTestId}-notes-input`}
                        value={draft}
                        onChange={(ev) => setDraft(ev.target.value)}
                        rows={3}
                        maxLength={4000}
                        placeholder="Hair, makeup, costume notes for this character in this scene…"
                        className="w-full bg-secondary/60 border border-white/10 rounded-md text-sm text-white p-2.5 leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                    />
                ) : notes ? (
                    <p
                        data-testid={`${dataTestId}-notes-display`}
                        className="text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed bg-secondary/30 border border-white/5 rounded-md p-2.5"
                    >
                        {notes}
                    </p>
                ) : (
                    <p
                        data-testid={`${dataTestId}-notes-empty`}
                        className="text-xs text-neutral-500 italic"
                    >
                        No notes yet — add hair / makeup / costume detail for this character in this scene.
                    </p>
                )}
                {notesUpdatedAt ? (
                    <p className="mt-1 text-[10px] text-neutral-500">
                        Updated {formatRelativeTime(notesUpdatedAt)}
                        {notesUpdatedByName ? ` · ${notesUpdatedByName}` : ""}
                    </p>
                ) : null}

                {/* Iter-118.15 — Look summary directly below Continuity
                    Notes. The Look is often the key continuity reference
                    for hair / makeup / costume; users shouldn't need to
                    open Entry Detail to read it. We surface every
                    distinct Look across the entries in this block,
                    de-duped + ordered by first appearance.
                    Iter-118.17 PART 1 — Add/Edit affordance so Look is
                    a first-class editable field. We don't build a new
                    editor — we route the user to the most-recent
                    entry's ImageDetail where the dedicated Look-edit
                    affordance already lives (iter-118.13). When the
                    block has 0 entries the affordance is hidden because
                    a Look needs an entry to attach to (capture flow
                    creates one). */}
                {(() => {
                    const distinctLooks = Array.from(
                        new Set(
                            (entries || [])
                                .map((e) => (e?.look || "").trim())
                                .filter(Boolean),
                        ),
                    );
                    // Most-recent entry (entries are usually sorted desc
                    // by created_at upstream; if not, pick the one with
                    // the latest timestamp). Used as the navigation target
                    // for the Edit/Add button.
                    const mostRecentEntry = (entries || []).slice().sort((a, b) => {
                        const at = new Date(a?.created_at || 0).getTime();
                        const bt = new Date(b?.created_at || 0).getTime();
                        return bt - at;
                    })[0];
                    const hasLook = distinctLooks.length > 0;
                    const canEditLook = !!mostRecentEntry;
                    return (
                        <div
                            data-testid={`${dataTestId}-look-section`}
                            className="mt-3"
                        >
                            <div className="flex items-center justify-between mb-1.5">
                                <h3 className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                    Look
                                </h3>
                                {canEditLook ? (
                                    <Link
                                        to={`${routeBase}/${projectId}/state-entries/${mostRecentEntry.id}`}
                                        state={{ backHref, backLabel }}
                                        data-testid={`${dataTestId}-look-edit`}
                                        className="tap inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-neutral-400 hover:text-white hover:bg-white/5"
                                    >
                                        <Pencil size={11} />
                                        {hasLook ? "Edit" : "Add"}
                                    </Link>
                                ) : null}
                            </div>
                            {!hasLook ? (
                                <p
                                    data-testid={`${dataTestId}-look-empty`}
                                    className="text-xs text-neutral-500 italic"
                                >
                                    No look set
                                </p>
                            ) : (
                                <div
                                    data-testid={`${dataTestId}-look-values`}
                                    className="flex flex-wrap gap-1.5"
                                >
                                    {distinctLooks.map((lk) => (
                                        <span
                                            key={lk}
                                            data-testid={`${dataTestId}-look-value-${lk}`}
                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/25"
                                        >
                                            {lk}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>

            {/* Iter-118.5 — Bulk-remove confirmation modal. The copy is
                deliberately precise: this removes ASSIGNMENT LINKS, not
                images. Photos linked elsewhere stay there; sole-link
                photos move to Unassigned (preserved, never destroyed). */}
            <AlertDialog
                open={confirmRemove}
                onOpenChange={(o) => !o && setConfirmRemove(false)}
            >
                <AlertDialogContent
                    data-testid={`${dataTestId}-bulk-remove-confirm`}
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-400">
                            Remove {selectedIds.size} selected image
                            {selectedIds.size === 1 ? "" : "s"} from this{" "}
                            {isCharacterHeader ? "character" : "scene"}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the assignment link
                            {selectedIds.size === 1 ? "" : "s"}. Images
                            linked elsewhere stay available there. Anything
                            linked only to this {isCharacterHeader ? "character" : "scene"}
                            {" "}is moved to Unassigned — nothing is permanently
                            deleted.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid={`${dataTestId}-bulk-remove-cancel`}
                            disabled={removing}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid={`${dataTestId}-bulk-remove-go`}
                            onClick={performBulkRemove}
                            disabled={removing}
                            className="bg-red-600 text-white hover:bg-red-500 font-semibold"
                        >
                            {removing ? "Removing…" : "Remove selected"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Iter-130b — Multi-image print layout chooser. Builds a
                Scene · Look · Notes-only sheet from the current
                selection. No project name, no IDs, no timestamps. */}
            <PrintLayoutDialog
                open={printDialogOpen}
                onOpenChange={setPrintDialogOpen}
                count={selectedIds.size}
                onPick={(layout) => {
                    const chosen = (entries || []).filter((e) =>
                        selectedIds.has(e.id),
                    );
                    if (chosen.length === 0) return;
                    const items = chosen.map((e) =>
                        buildPrintItem({
                            projectId,
                            entry: e,
                            scene,
                            // Per-pair notes are already loaded by the
                            // parent and live in the `notes` prop —
                            // reuse them so the print sheet reflects
                            // what the user sees on-screen.
                            notes,
                            apiBase,
                        }),
                    );
                    printContinuitySheet({ items, layout });
                    // Tear the select-mode chrome down after firing
                    // the job so the user lands back on a calm card.
                    exitSelectMode();
                }}
            />
        </section>
    );
}
